/**
 * @author : Mohammad Sarfaraz
 * @date : 25-Nov-2025
 * @description : Task management modal - Add and view related tasks
 */

import React, { useEffect, useState } from 'react';
import { Modal, Button, Tabs, Tab, Form, Alert } from "react-bootstrap";
import { FaTasks, FaExclamationTriangle } from 'react-icons/fa';
import toast from 'react-hot-toast';
import TaskWizard from '../task/TaskWizard';
import JajotApi from '../../api/JajotApi';
import { taskHeaders } from "../../constants/CONSTANT";
import { Shimmer } from "react-shimmer";
import RecordDataTable from "../RecordDataTable";
import RecordModal from './RecordModal';

const TaskModal = ({ show, onHide, records = [], defaultRecordId = null, header = "Record" }) => {
    const [activeTab, setActiveTab] = useState("add");
    const [selectedRecordId, setSelectedRecordId] = useState(defaultRecordId || '');
    const [taskRecords, setTaskRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [taskToEdit, setTaskToEdit] = useState(null);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    const resetModalState = () => {
        setActiveTab("add");
        setSelectedRecordId(defaultRecordId || '');
        setTaskRecords([]);
        setError(null);
        setSuccessMsg(null);
        setTaskToEdit(null);
    };

    useEffect(() => {
        if (!show) resetModalState();
    }, [show]);

    useEffect(() => {
        if (selectedRecordId) fetchTasks();
    }, [selectedRecordId]);

    const fetchTasks = async () => {
        if (!selectedRecordId) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await JajotApi.getTasksRecords(selectedRecordId, null);
            setTaskRecords(response?.success ? response.records || [] : []);
        } catch (err) {
            console.error('Error fetching tasks:', err);
            setError("Failed to load tasks");
            setTaskRecords([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTaskSuccess = (message = "Task saved successfully!") => {
        setSuccessMsg(message);
        setTaskToEdit(null);
        fetchTasks();
    };

    const handleEditTask = (task) => {
        const mappedTask = {
            Id: task?.Id,
            Name: task?.Name,
            Title: task?.Title__c,
            Status: task?.Status__c || "New",
            OwnerId: task?.OwnerId__c,
            StartDate: task?.Start_Date__c,
            EndDate: task?.End_Date__c,
            Priority: task?.Priority__c || "",
            Description: task?.Description__c,
            Assignees: task?.Task_Assignees__r?.records?.map(el => ({
                recordId: el?.Id,
                assigneeId: el?.ContactId__c,
            })) || [],
        };
        setTaskToEdit(mappedTask);
        setActiveTab("add");
    };

    const handleDeleteTask = async (id) => {
        try {
            const result = await JajotApi.deleteTaskBy(id);
            if (result.success) {
                toast.success("Task deleted successfully!");
                fetchTasks();
            } else {
                toast.error("Failed to delete task");
            }
        } catch (err) {
            console.error("Delete task error:", err);
            toast.error("Error deleting task");
        }
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        if (key === "list") setTaskToEdit(null);
    };

    const selectedRecord = records.find(r => r?.Id === selectedRecordId);

    const renderEmptyState = (message) => (
        <div className="text-center text-muted py-5">
            <FaExclamationTriangle size={32} className="mb-2 opacity-50" />
            <p className="mb-0">{message}</p>
        </div>
    );

    return (
        <>
            <Modal size="lg" show={show} onHide={onHide} centered scrollable>
                <Modal.Header closeButton className="bg-light border-bottom">
                    <div className="d-flex align-items-center">
                        <FaTasks size={24} className="me-3 text-primary" />
                        <div>
                            <Modal.Title>
                                <h3 className="mb-0 text-primary">Task Management</h3>
                            </Modal.Title>
                            {selectedRecord && (
                                <small className="text-muted">
                                    {header}: <strong>{selectedRecord.Name}</strong>
                                </small>
                            )}
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body>
                    <Form.Select
                        value={selectedRecordId}
                        onChange={(e) => {
                            setSelectedRecordId(e.target.value);
                            setError(null);
                            setTaskToEdit(null);
                        }}
                        disabled={isLoading}
                        className="mb-3"
                    >
                        <option value="">Select {header}</option>
                        {records.map(row => (
                            <option key={row.Id} value={row.Id}>{row.Name}</option>
                        ))}
                    </Form.Select>

                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                            <FaExclamationTriangle className="me-2" />{error}
                        </Alert>
                    )}

                    {successMsg && (
                        <Alert variant="success" dismissible onClose={() => setSuccessMsg(null)} className="mb-3">
                            {successMsg}
                        </Alert>
                    )}

                    <Tabs activeKey={activeTab} onSelect={handleTabChange}>
                        <Tab eventKey="add" title={taskToEdit ? "Edit Task" : "Add Task"}>
                            {!selectedRecordId ? (
                                renderEmptyState("Select a record to continue")
                            ) : (
                                <>
                                    {taskToEdit && (
                                        <div className="d-flex justify-content-between align-items-center ps-2 pt-4">
                                            <small className="text-muted">
                                                Editing: {taskToEdit.Title || taskToEdit.Name}
                                            </small>
                                            <Button size="sm" variant="outline-secondary" onClick={() => setTaskToEdit(null)}>
                                                Clear & Create New
                                            </Button>
                                        </div>
                                    )}
                                    <TaskWizard
                                        show={true}
                                        hideHeader={false}
                                        relatedToId={selectedRecordId}
                                        onTaskCreated={() => handleTaskSuccess()}
                                        taskToEdit={taskToEdit}
                                    />
                                </>
                            )}
                        </Tab>

                        <Tab
                            eventKey="list"
                            title={
                                <>
                                    Tasks {taskRecords.length > 0 && <span className="badge bg-primary ms-1">{taskRecords.length}</span>}
                                </>
                            }
                        >
                            {!selectedRecordId ? (
                                renderEmptyState("Select a record to view tasks")
                            ) : isLoading ? (
                                <div className="py-3">
                                    <Shimmer width={-1} height={30} className="mb-3" />
                                    <Shimmer width={-1} height={20} className="mb-2" />
                                    <Shimmer width={-1} height={20} className="mb-2" />
                                    <Shimmer width={-1} height={20} />
                                </div>
                            ) : taskRecords.length > 0 ? (
                                <div className="mt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <small className="text-muted">
                                            {taskRecords.length} task{taskRecords.length !== 1 ? 's' : ''}
                                        </small>
                                        <Button size="sm" variant="outline-primary" onClick={fetchTasks} disabled={isLoading}>
                                            Refresh
                                        </Button>
                                    </div>
                                    <RecordDataTable
                                        tableHeaders={taskHeaders}
                                        tableData={taskRecords}
                                        handleRowClick={setSelectedTask}
                                        permission={{ update: true, delete: true }}
                                        handleEdit={handleEditTask}
                                        onDelete={handleDeleteTask}
                                    />
                                </div>
                            ) : (
                                <div className="text-center text-muted py-5">
                                    <FaTasks size={48} className="mb-3 opacity-25" />
                                    <h6>No tasks found</h6>
                                    <small>Create a new task in the "Add Task" tab</small>
                                </div>
                            )}
                        </Tab>
                    </Tabs>
                </Modal.Body>

                <Modal.Footer className="bg-light">
                    <Button size="sm" variant="secondary" onClick={onHide}>Close</Button>
                </Modal.Footer>
            </Modal>

            {selectedTask && (
                <RecordModal
                    show={true}
                    onHide={() => setSelectedTask(null)}
                    record={selectedTask}
                    header={taskHeaders}
                />
            )}
        </>
    );
};

export default TaskModal;